import mixpanel from 'mixpanel-browser';

// Initialize mixpanel with your project token
const initMixpanel = () => {
  const token = import.meta.env.VITE_MIXPANEL_PROJECT_TOKEN;
  if (token) {
    mixpanel.init(token, {
      debug: import.meta.env.VITE_NODE_ENV !== 'production',
      persistence: 'localStorage'
    });
    return true;
  }
  return false;
};

// Generate a unique ID for the user based on their account information
export const generateDistinctId = (
  userId: string | number | null | undefined, 
  currentSocial: any
) => {
  if (!userId) return null;
  
  // If we have social account information, use it for more accurate identification
  if (currentSocial?.providerName && currentSocial?.providerId) {
    return `${currentSocial.providerName}_${currentSocial.providerId}_${userId}`;
  }
  
  // Fallback to just userId if no social info is available
  return `user_${userId}`;
};

// Identify the user in Mixpanel
export const identifyUser = (
  userId: string | number | null | undefined, 
  currentSocial: any
) => {
  const distinctId = generateDistinctId(userId, currentSocial);
  if (!distinctId) return null;
  
  mixpanel.identify(distinctId);
  
  // Set user properties
  const userProperties: Record<string, any> = {
    $userId: userId
  };
  
  if (currentSocial) {
    if (currentSocial.providerName) userProperties.$name = currentSocial.providerName;
    if (currentSocial.provider) userProperties.platform = currentSocial.provider;
    if (currentSocial.profilePictureUrl) userProperties.$avatar = currentSocial.profilePictureUrl;
  }
  
  mixpanel.people.set(userProperties);
  
  return distinctId;
};

// Generic event tracking method for any event
export const trackEvent = (eventName: string, properties?: Record<string, any>) => {
  mixpanel.track(eventName, properties);
};

// Track onboarding step
export const trackOnboardingStep = (stepNumber: number, stepName: string, additionalProperties?: Record<string, any>) => {
  mixpanel.track('Onboarding Step', {
    step_number: stepNumber,
    step_name: stepName,
    ...additionalProperties
  });
};

// Track onboarding completion
export const trackOnboardingComplete = (additionalProperties?: Record<string, any>) => {
  mixpanel.track('Onboarding Complete', additionalProperties);
};

// Track onboarding abandonment
export const trackOnboardingAbandoned = (lastStep: number, lastStepName: string, additionalProperties?: Record<string, any>) => {
  mixpanel.track('Onboarding Abandoned', {
    last_step: lastStep,
    last_step_name: lastStepName,
    ...additionalProperties
  });
};

// Track specific onboarding actions
export const trackOnboardingAction = (action: string, properties?: Record<string, any>) => {
  mixpanel.track(`Onboarding Action: ${action}`, properties);
};

// Configure Mixpanel tracking based on environment
export const configureMixpanelTracking = () => {
  const isInitialized = initMixpanel();
  
  // Only track if Mixpanel is properly initialized
  if (!isInitialized) {
    // Create no-op implementations for all tracking functions to avoid errors
    return {
      identifyUser: () => null,
      trackEvent: () => {},
      trackOnboardingStep: () => {},
      trackOnboardingComplete: () => {},
      trackOnboardingAbandoned: () => {},
      trackOnboardingAction: () => {}
    };
  }
  
  return {
    identifyUser,
    trackEvent,
    trackOnboardingStep,
    trackOnboardingComplete,
    trackOnboardingAbandoned,
    trackOnboardingAction
  };
};

// Export a ready-to-use tracker
export const mixpanelTracker = configureMixpanelTracking();